import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge, Button, Checkbox, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@clouway/ui'
import { ChevronDown, ChevronRight } from 'lucide-react'

import { domainDisplayName } from '../transform/domainDisplayName'
import { tierVerbsByResource, toggleVerb } from '../transform/membership'
import { resourceDisplayName } from '../transform/resourceDisplayName'
import { verbLabel } from '../transform/verbLabel'
import type { DomainDef, DomainState, ProductStructure, TierId } from '../types'

const TIERS: TierId[] = ['viewer', 'editor', 'admin']

// Rows = domains, columns = None/Viewer/Editor/Admin. Each column is a plain
// toggle button rather than a real <input type="radio"> — a RadioGroup would
// have to wrap the row's <td> cells in its own container div, which isn't
// valid inside a <tr>. Selecting a tier replaces the row's state outright;
// selecting an individual verb in the advanced expander converts the row to
// "custom" (see transform/membership.ts toggleVerb).
export function TierMatrix({
  structure,
  states,
  onChange,
  readOnly = false,
}: {
  structure: ProductStructure
  states: Record<string, DomainState>
  onChange: (domain: string, state: DomainState) => void
  readOnly?: boolean
}) {
  const { t } = useTranslation('feature')

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('role.matrix.domain')}</TableHead>
          <TableHead>{t('role.matrix.none')}</TableHead>
          {TIERS.map((tier) => (
            <TableHead key={tier}>{t(`role.matrix.${tier}`)}</TableHead>
          ))}
          <TableHead className="w-10" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {structure.domains.map((domainDef) => (
          <DomainRows
            key={domainDef.domain}
            structure={structure}
            domainDef={domainDef}
            state={states[domainDef.domain] ?? { kind: 'none' }}
            onChange={(next) => onChange(domainDef.domain, next)}
            readOnly={readOnly}
          />
        ))}
      </TableBody>
    </Table>
  )
}

function DomainRows({
  structure,
  domainDef,
  state,
  onChange,
  readOnly,
}: {
  structure: ProductStructure
  domainDef: DomainDef
  state: DomainState
  onChange: (state: DomainState) => void
  readOnly: boolean
}) {
  const { t, i18n } = useTranslation('feature')
  const [expanded, setExpanded] = useState(false)

  const selectedVerbs =
    state.kind === 'custom' ? state.verbsByResource : state.kind === 'tier' ? tierVerbsByResource(structure, domainDef.domain, state.tier) : {}
  const displayName = domainDisplayName(domainDef, i18n.language)

  return (
    <>
      <TableRow>
        <TableCell className="font-medium">
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              {displayName}
              {state.kind === 'custom' && (
                <Badge variant="outline" className="text-muted-foreground">
                  {t('role.matrix.custom')}
                </Badge>
              )}
            </div>
            {displayName !== domainDef.domain && (
              <span className="text-muted-foreground font-mono text-xs">{domainDef.domain}</span>
            )}
          </div>
        </TableCell>
        <TierButton label={t('role.matrix.none')} active={state.kind === 'none'} readOnly={readOnly} onClick={() => onChange({ kind: 'none' })} />
        {TIERS.map((tier) => (
          <TierButton
            key={tier}
            label={t(`role.matrix.${tier}`)}
            active={state.kind === 'tier' && state.tier === tier}
            readOnly={readOnly}
            onClick={() => onChange({ kind: 'tier', tier })}
          />
        ))}
        <TableCell>
          <Button type="button" size="icon" variant="ghost" aria-label={t('role.matrix.advanced')} onClick={() => setExpanded((value) => !value)}>
            {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          </Button>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={TIERS.length + 2} className="bg-muted/30">
            <div className="flex flex-col gap-2 py-1">
              <span className="text-muted-foreground text-xs font-medium">{t('role.matrix.advanced')}</span>
              {domainDef.resources.map((resource) => (
                <div key={resource.id} className="flex flex-wrap items-center gap-3">
                  {/* The raw id doubles as a title tooltip rather than its own line — this row is
                      too compact to stack name + mono id the way the domain row above does. */}
                  <span className="text-muted-foreground w-32 shrink-0 truncate text-xs" title={resource.id}>
                    {resourceDisplayName(resource, i18n.language)}
                  </span>
                  {resource.verbs.map((verb) => {
                    const checked = (selectedVerbs[resource.id] ?? []).includes(verb)
                    return (
                      <label key={verb} className="flex items-center gap-1 text-xs">
                        <Checkbox
                          checked={checked}
                          disabled={readOnly}
                          onCheckedChange={() => onChange(toggleVerb(structure, domainDef.domain, state, resource.id, verb))}
                        />
                        {verbLabel(t, verb)}
                      </label>
                    )
                  })}
                </div>
              ))}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

function TierButton({ label, active, readOnly, onClick }: { label: string; active: boolean; readOnly: boolean; onClick: () => void }) {
  return (
    <TableCell>
      <Button type="button" size="sm" variant={active ? 'default' : 'outline'} disabled={readOnly} aria-pressed={active} onClick={onClick}>
        {label}
      </Button>
    </TableCell>
  )
}
